import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { Button, LoadingBlock, Notice, PageHeader, Section, TextArea, useShortcut } from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { admitPatient } from '../api/inpatient.ts';
import { can, sessionQuery } from '../api/session.ts';
import { visitQuery } from '../api/visits.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ShortcutHint } from '../layout/screen.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { toasts } from '../toasts.ts';
import { errorText } from './bed-board-page.tsx';
import styles from './inpatient.module.css';
import { NO_BED, PlacePicker, useBedNeedsCleaning } from './place-picker.tsx';

// Admit a patient from their open visit (docs/plans/phase2-inpatient-adt.md §6). Opens the
// inpatient visit and puts the patient in a bed, or on the ward's waiting list.

const route = getRouteApi('/_app/visits/$visitId/admit');

export function AdmitPage() {
  const { visitId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const visit = useQuery(visitQuery(visitId));
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [wardId, setWardId] = useState<string | null>(null);
  const [bedId, setBedId] = useState<string>(NO_BED);
  const [reason, setReason] = useState('');
  const [cleaningReason, setCleaningReason] = useState('');
  const needsCleaning = useBedNeedsCleaning(wardId, bedId);
  const mayAdmit = session !== null && can(session, 'inpatient.admit');

  const admit = useMutation({
    mutationFn: () =>
      admitPatient(visitId, {
        clientRequestId,
        wardId: wardId ?? '',
        ...(bedId === NO_BED ? {} : { bedId }),
        reason: reason.trim(),
        ...(needsCleaning ? { cleaningOverrideReason: cleaningReason.trim() } : {}),
      }),
    onSuccess: async (stay) => {
      await queryClient.invalidateQueries({ queryKey: ['inpatient'] });
      toasts.show({
        title: 'Patient admitted',
        description:
          stay.current?.bed === null
            ? 'Waiting for a bed'
            : `Bed ${stay.current?.bed?.label ?? ''}`,
      });
      await navigate({ to: '/admissions/$admissionId', params: { admissionId: stay.id } });
    },
  });
  const ready =
    online &&
    wardId !== null &&
    reason.trim() !== '' &&
    (!needsCleaning || cleaningReason.trim().length >= 5) &&
    !admit.isPending;
  useShortcut(
    'mod+enter',
    () => {
      if (ready) admit.mutate();
    },
    { allowInFields: true, enabled: mayAdmit, description: 'Admit patient' },
  );

  if (visit.isPending) return <LoadingBlock label="Loading the visit" />;
  if (visit.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void visit.refetch()}>Try again</Button>}>
        {errorText(visit.error, "The visit couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const openStay =
    admit.error instanceof ApiError && admit.error.code === 'admission_already_open'
      ? admit.error.context?.['admissionId']
      : undefined;
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={visit.data.patientId} />
      <PageHeader
        title="Admit patient"
        description={`From visit ${visit.data.visitNumber}. The stay gets a visit of its own; this visit stays open until it is finished.`}
      />
      {!mayAdmit ? (
        <Notice tone="info">
          You don't have permission to admit patients. Ask a clinician to admit.
        </Notice>
      ) : visit.data.status !== 'open' ? (
        <Notice tone="info">
          This visit is closed or cancelled. Admit the patient from an open visit.
        </Notice>
      ) : (
        <Section title="Where and why">
          <form
            className={styles.form}
            aria-label="Admit patient"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready) admit.mutate();
            }}
          >
            <PlacePicker
              wardId={wardId}
              bedId={bedId}
              onWard={(id) => {
                setWardId(id);
                setBedId(NO_BED);
              }}
              onBed={setBedId}
              cleaningReason={cleaningReason}
              onCleaningReason={setCleaningReason}
            />
            <TextArea
              label="Reason for admission"
              description="Kept with the stay and shown to the ward team."
              value={reason}
              onChange={setReason}
              rows={4}
            />
            {!online ? (
              <Notice tone="info">
                No connection to Wodi. Admitting waits for the connection; nothing is saved on this
                device.
              </Notice>
            ) : null}
            {admit.error === null ? null : (
              <Notice
                tone="error"
                {...(typeof openStay === 'string'
                  ? {
                      action: (
                        <Link to="/admissions/$admissionId" params={{ admissionId: openStay }}>
                          Open stay
                        </Link>
                      ),
                    }
                  : {})}
              >
                {errorText(admit.error, "The patient wasn't admitted. Try again.")}
              </Notice>
            )}
            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                isDisabled={!ready}
                isPending={admit.isPending}
                pendingLabel="Admitting…"
              >
                Admit patient
              </Button>
              <ShortcutHint combo="mod+enter" action="admit" />
            </div>
          </form>
        </Section>
      )}
    </div>
  );
}
